import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL, LOGOUT_API, REFRESH_TOKEN_API } from "./config";
import {
  GetRefreshToken,
  GetToken,
  RemoveRefreshToken,
  RemoveToken,
  SetRefreshToken,
  SetToken,
  forceLogout,
  readTokenExpiry,
} from "../helper/helper";
import { clearNotificationBell } from "../helper/notifications";

/**
 * The single axios instance every request in the app goes through.
 *
 * Replaces the old APIConfig.js, which registered the same ideas on the global axios but was
 * never imported (App.js had it commented out), so none of it ever ran. Three things are fixed
 * here that made that file unsafe to simply switch on:
 *
 *   1. It read `error.response.status` unguarded, so any request that fails with no reply at
 *      all - offline, blocked, DNS failure, and now a timeout - threw inside the interceptor
 *      and replaced the real error. The no-response branch below runs first, before anything
 *      touches `error.response`.
 *   2. Its refresh awaited a debounced RefreshToken() that returned undefined, so the retry was
 *      unreachable. Refresh here is a plain call on raw axios (see refreshSession).
 *   3. Nothing serialised concurrent refreshes. The server rotates the refresh token on every
 *      call, so parallel refreshes invalidate each other - and every open tab shares the same
 *      stored pair, so "parallel" includes other tabs. See refreshSession.
 *
 * SESSION RENEWAL LIVES HERE AND NOWHERE ELSE (#104). Components never refresh a token: the 401
 * handler below renews on demand, a timer renews ahead of expiry (armRenewal), SignalR asks
 * getAccessToken() for a token on every connect, and PrivateRoute's guard joins the same
 * refreshSession() when navigation finds an expired token. The debounced RefreshToken export in
 * auth-service.js, and the two components that called it, are gone.
 *
 * Note there is deliberately NO baseURL: every wrapper in auth-service.js builds an absolute
 * `BASE_URL + CONSTANT` url, which axios would leave alone anyway, and a baseURL here would
 * turn into a double-prefix bug the moment someone removed that concatenation.
 */

/** Ceiling for an ordinary request. Past this the server is not answering and the user is told. */
export const DEFAULT_TIMEOUT = 30000;

/** Image upload moves real bytes over slow uplinks, so it gets its own, longer allowance. */
export const UPLOAD_TIMEOUT = 120000;

const MAX_RETRIES = 2;
const RETRY_BASE_DELAY = 500;

/**
 * How long before the earlier of the two expiries the renewal timer fires. The server enforces
 * the refresh token's own lifetime, so a session that sends no request for longer than that - a
 * user watching a live auction over SignalR - would otherwise be refused at the next click.
 */
const RENEW_AHEAD_MS = 60000;

/**
 * The shortest gap between two proactive attempts. It throttles the retry after a failed renewal,
 * and stops a token issued with a lifetime under RENEW_AHEAD_MS from turning the timer into a loop.
 */
const RENEW_MIN_INTERVAL_MS = 60000;

/**
 * How long a sign-out waits for the server to revoke the session. Short, because the user is
 * watching the Logout click do nothing until it answers; past it the sign-out goes ahead locally.
 */
const LOGOUT_TIMEOUT = 5000;

/** getAccessToken treats a token this close to its `exp` as expired: the server's ClockSkew is 0. */
const EXPIRY_MARGIN_MS = 30000;

/** The Web Locks name every tab of this origin serialises its refresh on. */
const REFRESH_LOCK = "xcutta-session-refresh";

/** Shown on the sign-in screen when the server refuses to renew the session. */
export const SESSION_ENDED_MESSAGE = "Your session has ended. Please sign in again.";

/**
 * GET is our proxy for "safe to repeat", but one endpoint breaks that rule: it is a GET that
 * changes state, so repeating it would re-run a Stripe reconcile. A new state-changing endpoint
 * should be a POST; if it cannot be, add it here or pass `{ noRetry: true }` at the call site.
 *
 * Common/RedeemPromoCode used to be listed here. It is a POST now, so the retry wrapper skips it
 * on the verb alone - which matters more than it used to: repeating a redemption was a harmless
 * "already availed" while a code was single-use, and is a real double-claim now that codes are
 * multi-use.
 */
const NON_IDEMPOTENT_GETS = ["Stripe/GetPaymentStatus"];

const TOAST_TIMEOUT = "request-timeout";
const TOAST_NETWORK = "request-network";
const TOAST_SERVER = "request-server";

const api = axios.create({
  timeout: DEFAULT_TIMEOUT,
  headers: {
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
  },
});

const isTimeout = (error) =>
  error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT";

/**
 * Reads a response header regardless of how axios handed it back.
 *
 * Axios 1.x normally gives an AxiosHeaders instance with lowercased keys, but the shape varies
 * by adapter and version, and this one header decides whether a 401 means "expired" or "not
 * allowed" - getting it wrong would silently disable session renewal.
 */
const readHeader = (headers, name) => {
  if (!headers) return undefined;
  if (typeof headers.get === "function") return headers.get(name);
  return headers[name.toLowerCase()] ?? headers[name];
};

/**
 * Attaches the bearer token to every outgoing request.
 *
 * This replaces the 138 hand-built `Authorization` headers that used to live in
 * auth-service.js. A request can opt out with `{ skipAuth: true }`.
 */
api.interceptors.request.use(
  (config) => {
    const accessToken = GetToken();
    if (accessToken && !config.skipAuth) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    // Starts the renewal timer after sign-in, and again if it lapsed. A no-op while one is pending.
    if (accessToken) armRenewal();
    return config;
  },
  (error) => Promise.reject(error)
);

const isGet = (config) => (config?.method || "get").toLowerCase() === "get";

/**
 * Whether a failed response carries a sentence the user can be shown. ExceptionMiddleware's 500
 * body does; a gateway's 502/503/504 page, which is HTML, does not.
 */
const hasServerMessage = (response) =>
  typeof response?.data?.message === "string" && response.data.message.trim() !== "";

/** Whether this specific request may be sent again without changing anything twice. */
const canRetry = (config, error) => {
  if (!config || config.noRetry) return false;
  if (!isGet(config)) return false;
  if (NON_IDEMPOTENT_GETS.some((path) => (config.url || "").includes(path))) return false;
  if ((config.__retryCount || 0) >= MAX_RETRIES) return false;

  // No reply at all, or the server broke. A 4xx is an answer - repeating it changes nothing.
  return !error.response || error.response.status >= 500;
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Waits out the backoff for the next attempt, then reissues the request. */
const retryRequest = async (config) => {
  config.__retryCount = (config.__retryCount || 0) + 1;
  const backoff = RETRY_BASE_DELAY * 2 ** (config.__retryCount - 1);
  await delay(backoff + Math.random() * 250);
  return api(config);
};

/** Runs `task` holding the cross-tab refresh lock, or directly where Web Locks is missing. */
const withRefreshLock = (task) => {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  return locks?.request ? locks.request(REFRESH_LOCK, task) : task();
};

/**
 * The refresh itself, run while holding the lock. See refreshSession.
 *
 * #182: says WHY it failed. `refused` is true only when the server answered no - the session is
 * over; a request that got no reply, timed out or hit a 5xx is not a refusal, and the session may
 * well still be good. They used to come back as the same null.
 *
 * @param {string|null} staleToken The access token the caller wanted replaced.
 * @returns {Promise<{token: string|null, refused: boolean}>}
 */
const renewSession = async (staleToken) => {
  const current = GetToken();
  // Another tab renewed while this one waited for the lock, and its pair is already stored.
  if (current && current !== staleToken) return { token: current, refused: false };

  // GetRefreshToken already reads a stored "null" or "undefined" as no token. The expired access
  // token is needed as well: the server takes the account's email from it.
  const refreshToken = GetRefreshToken();
  if (!current || !refreshToken) return { token: null, refused: false };

  try {
    const response = await axios.post(
      BASE_URL + REFRESH_TOKEN_API,
      { status: true, token: current, refreshToken, message: "string" },
      // Bounded because the lock is held for as long as this takes: a request that never settled
      // would stall renewal in every tab.
      { timeout: DEFAULT_TIMEOUT }
    );
    const token = response?.data?.token;
    if (!token) return { token: null, refused: false };
    SetToken(token);
    SetRefreshToken(response.data.refreshToken);
    armRenewal(true);
    return { token, refused: false };
  } catch (error) {
    // Account/RefreshToken answers 401 for a forged access token or a rotated, revoked or expired
    // refresh token, and 400 for missing input: the session is over - unless a tab that does not
    // take the lock rotated the pair while this request was out, in which case the refusal is
    // only this tab being late, and what is stored now is the renewed session.
    const stored = GetToken();
    if (stored && GetRefreshToken() !== refreshToken) return { token: stored, refused: false };
    const status = error?.response?.status;
    return { token: null, refused: status === 400 || status === 401 };
  }
};

/**
 * Renews the session, at most once at a time - across every open tab, not just this one.
 *
 * Deliberately uses raw `axios`, not `api`: a failing refresh routed through this instance
 * would recurse into its own 401 handler. The body field is `token` (not `accessToken`), and
 * `refreshToken` is never sent null. The server now refuses a missing or expired refresh token
 * outright, but it used to compare it against a column that is NULL after logout, so a null
 * matched and minted a fresh pair for a signed-out account.
 *
 * TWO TABS. The promise below makes this single-flight within one tab, but every tab reads and
 * writes the same stored pair. Two tabs meeting an expired token together would send the same
 * refresh token; the server rotates it on the first and refuses the second, and the second tab's
 * forceLogout then clears the shared storage and signs every tab out. So the work runs under a Web
 * Lock, and a tab that gets the lock second first checks whether the stored token is still the one
 * it wanted replaced - if not, another tab already renewed and there is nothing to send. A tab that
 * does not take the lock (no Web Locks, or an older build left open across a deploy) can still win
 * the race, which renewSession allows for by not believing a refusal once the stored refresh token
 * has changed underneath it.
 *
 * A REFUSAL ENDS THE SESSION HERE (#182), whoever asked: forceLogout clears it and the sign-in
 * screen says why. The proactive timer and SignalR's token factory used to swallow a refusal - the
 * live feed then retried with a dead token every few seconds, with nothing on screen - and only
 * the next HTTP request's 401 ended the session. A failure that is not a refusal (no reply, 5xx)
 * ends nothing: the next attempt tries again.
 *
 * @param {string|null} [staleToken] The access token the caller found wanting - for the 401
 *   handler, the one the refused request carried. Defaults to the stored token.
 * @returns {Promise<string|null>} The access token to use from now on, or null when the session
 *   could not be renewed (refused - and then already ended - or no reply). Never rejects.
 */
let refreshPromise = null;

/**
 * #182: whether the last renewal of a live session failed without the server refusing it - no
 * reply, a timeout, a 5xx. The session is kept (the next attempt may well work), but the token may
 * run out meanwhile and the live feed with it, so the player is told (LiveFeedBanner). Cleared by
 * the next renewal that works - in this tab or, found in storage, another - and by a sign-out. A
 * refusal needs no flag: it ends the session (forceLogout) and the sign-in screen says why.
 */
let sessionRenewalFailing = false;
const renewalHealthListeners = new Set();

const setSessionRenewalFailing = (failing) => {
  if (sessionRenewalFailing === failing) return;
  sessionRenewalFailing = failing;
  renewalHealthListeners.forEach((listener) => listener(failing));
};

/** @returns {boolean} Whether the session currently cannot be renewed (see sessionRenewalFailing). */
export const isSessionRenewalFailing = () => sessionRenewalFailing;

/**
 * Calls `listener(failing)` whenever that changes. ContextProvider publishes it to the screens.
 *
 * @param {(failing: boolean) => void} listener
 * @returns {() => void} Unsubscribes.
 */
export const subscribeSessionRenewal = (listener) => {
  renewalHealthListeners.add(listener);
  return () => renewalHealthListeners.delete(listener);
};

export const refreshSession = (staleToken) => {
  if (refreshPromise) return refreshPromise;

  const stale = staleToken || GetToken();
  refreshPromise = withRefreshLock(() => renewSession(stale))
    .then(({ token, refused }) => {
      if (refused) forceLogout(SESSION_ENDED_MESSAGE);
      // Failing only while there is still a session to renew: not after a refusal or a sign-out.
      setSessionRenewalFailing(!token && !refused && Boolean(GetToken()));
      return token;
    })
    .catch(() => {
      setSessionRenewalFailing(Boolean(GetToken()));
      return null;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

/**
 * The access token to connect with, renewed first when it is about to expire.
 *
 * This is SignalR's accessTokenFactory. The client calls it on every start and on every automatic
 * reconnect, so a hub reconnecting after the token expired connects with the renewed one - the
 * provider used to capture the token once, and every reconnect after expiry was refused. A
 * renewal the server refuses ends the session (refreshSession), which also stops the hub's retries;
 * one that gets no reply hands back the old token, and the hub retries later.
 *
 * @returns {Promise<string>} The token, or "" when signed out.
 */
export const getAccessToken = async () => {
  const token = GetToken();
  if (!token) return "";

  const expiry = readTokenExpiry(token);
  if (expiry !== null && expiry - EXPIRY_MARGIN_MS <= Date.now()) {
    return (await refreshSession(token)) || token;
  }
  return token;
};

let renewalTimer = null;
let lastProactiveAttempt = 0;

/**
 * When the next proactive renewal is due, in epoch milliseconds, or null when there is nothing to
 * renew: signed out, an access token with no `exp` (left to the server rather than renewed
 * blindly), or a refresh token already past its own - the next request ends that session.
 */
const renewalDue = () => {
  const refreshToken = GetRefreshToken();
  const accessExpiry = readTokenExpiry(GetToken());
  if (!refreshToken || accessExpiry === null) return null;

  const refreshExpiry = readTokenExpiry(refreshToken);
  if (refreshExpiry !== null && refreshExpiry <= Date.now()) return null;

  return Math.min(accessExpiry, refreshExpiry ?? Infinity) - RENEW_AHEAD_MS;
};

/**
 * The timer's callback. A dropped connection here signs nobody out - the timer tries again a minute
 * later; a refusal does, inside refreshSession (#182).
 */
const renewIfDue = async () => {
  renewalTimer = null;
  const due = renewalDue();
  if (due === null) return;

  // Another tab renewed since this timer was set, so the stored pair now expires later.
  if (due > Date.now()) {
    setSessionRenewalFailing(false);
    armRenewal();
    return;
  }

  lastProactiveAttempt = Date.now();
  await refreshSession();
  armRenewal();
};

/**
 * Ends the session on the server, then removes the stored token pair.
 *
 * FE-05: signing out used to clear localStorage and nothing else. Account/Logout is what nulls the
 * account's stored refresh token and SessionStamp - and the stamp check in the API's
 * OnTokenValidated refuses every access token issued for the session once it is gone - so without
 * this call a pair copied before the click went on working, and could be rotated indefinitely.
 *
 * Raw `axios` and no Bearer header, like renewSession: the endpoint is [AllowAnonymous] and reads
 * the access token from the body, expired or not. Through `api` a refusal would toast, and a 401
 * carrying Token-Expired would start a refresh of the very session being ended.
 *
 * Runs under the refresh lock, so no tab is halfway through rotating the pair while it is revoked
 * and cleared: a renewal that was waiting finds nothing stored and gives up, and the server would
 * refuse it anyway.
 *
 * Best effort: the pair is removed whatever the server says or however long it takes, so the user
 * is signed out of this browser even when the API is unreachable.
 *
 * @returns {Promise<void>} Never rejects.
 */
export const revokeSession = () =>
  withRefreshLock(async () => {
    const token = GetToken();
    const refreshToken = GetRefreshToken();

    if (token && refreshToken) {
      try {
        await axios.post(
          BASE_URL + LOGOUT_API,
          { status: true, token, refreshToken, message: "string" },
          { timeout: LOGOUT_TIMEOUT }
        );
      } catch {
        // Already ended (a later sign-in replaced it), refused, or no reply - signed out here either way.
      }
    }

    RemoveToken();
    RemoveRefreshToken();
    // #196: the bell's auction reminders belong to the session being ended.
    clearNotificationBell();
    clearTimeout(renewalTimer);
    renewalTimer = null;
    setSessionRenewalFailing(false);
  }).catch(() => {});

/**
 * Schedules the proactive renewal ahead of the earlier of the two expiries.
 *
 * Every tab runs one, and they all come due together because they read the same stored pair; the
 * lock in refreshSession makes the first renew and the rest find the work already done. Idle users
 * are not kept signed in by this - TopNavbar's inactivity timer signs them out.
 *
 * @param {boolean} [reschedule] Replace a pending timer, after a renewal moved the expiry.
 */
const armRenewal = (reschedule = false) => {
  if (renewalTimer !== null) {
    if (!reschedule) return;
    clearTimeout(renewalTimer);
    renewalTimer = null;
  }

  const due = renewalDue();
  if (due === null) return;

  const at = Math.max(due, lastProactiveAttempt + RENEW_MIN_INTERVAL_MS);
  renewalTimer = setTimeout(renewIfDue, Math.max(at - Date.now(), 0));
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;

    // ---- 1. No reply at all -------------------------------------------------------------
    // Must come first: `error.response` is undefined here, and reading through it is exactly
    // what crashed the old interceptor. Timeouts land in this branch too.
    if (!error.response) {
      if (axios.isCancel(error)) return Promise.reject(error);

      if (canRetry(config, error)) return retryRequest(config);

      toast.error(
        isTimeout(error)
          ? "The server took too long to respond. Please try again."
          : "Cannot reach the server. Check your connection and try again.",
        { toastId: isTimeout(error) ? TOAST_TIMEOUT : TOAST_NETWORK }
      );
      return Promise.reject(error);
    }

    const { status, headers } = error.response;

    // ---- 2. Session renewal -------------------------------------------------------------
    // `Token-Expired` is the only thing separating an expired token from a CustomAuthorize
    // permission failure - both are bodiless 401s. The header is CORS-exposed by the API.
    // Without it we fall through and treat the 401 as a permission problem, which is the safe
    // default: it shows the caller's own message instead of signing anyone out. A refresh
    // could not have fixed a permission failure anyway, because the server copies the AuthCode
    // claim verbatim onto the new token.
    if (status === 401 && readHeader(headers, "Token-Expired") && config && !config._retry) {
      // A signed-out visitor on a public page (Home fires an authenticated call) has no session
      // to renew and must not be bounced to the sign-in screen.
      if (!GetToken()) return Promise.reject(error);

      config._retry = true;

      // The token this request carried rather than the stored one: if a renewal has replaced it
      // since - in this tab or another - refreshSession hands that back instead of rotating again.
      const sentToken = String(readHeader(config.headers, "Authorization") || "").replace(
        /^Bearer\s+/i,
        ""
      );
      const renewed = await refreshSession(sentToken);
      if (renewed) return api(config);

      // #182: a refused renewal has already ended the session (refreshSession). No reply is not a
      // refusal: the session is kept, this request fails as it is, and the next one tries again.
      return Promise.reject(error);
    }

    // ---- 3. Server-side failure ---------------------------------------------------------
    if (status >= 500) {
      if (canRetry(config, error)) return retryRequest(config);

      // A database fault reaches a mutation as a 500 whose body says what went wrong, and the
      // caller shows that sentence just as it shows a 200 refusal - this toast on top would say
      // the same thing twice. It stays for a GET, whose caller reads data rather than a message,
      // and for a body with nothing readable in it.
      if (isGet(config) || !hasServerMessage(error.response)) {
        toast.error("Something went wrong on our side. Please try again.", {
          toastId: TOAST_SERVER,
        });
      }
    }

    // Everything else - including a permission 401 - is handed back untouched, so the callers
    // that already inspect it (Account_settings.js reads res.response.status) keep working.
    return Promise.reject(error);
  }
);

// A reload with a live session has no sign-in to start the timer, so it starts here.
armRenewal();

export default api;
